/**
 * Lost sheep coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_bible_stories`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const lostSheepColoringPagesPack: ToilePack = {
    slug: 'lost-sheep-coloring-pages',
    category: 'faith',
    name: 'Lost sheep coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'flock', label: 'The ninety-nine', subject: 'a shepherd counting his flock in a fold of stone walls, one place in the line of sheep empty'},
        {key: 'search', label: 'Searching', subject: 'the shepherd climbing among rocks and thorn bushes, finding the little lamb caught in a thicket'},
        {key: 'home', label: 'Carried home', subject: 'the shepherd carrying the lamb home on his shoulders, his friends and neighbours coming out to rejoice'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and simple', style: 'bold thick-outline coloring book style, one heavy uniform black line, big simple open shapes for young children', palette: 'black ink only', framing: 'The drawing centered with a wide white margin on all sides'},
        {key: 'classic', label: 'Classic', style: 'classic coloring book line art, medium uniform black line, moderate detail and open shapes for ages six to nine', palette: 'black ink only', framing: 'The full page used, the drawing centered with a white margin'},
        {key: 'detailed', label: 'Fine detail', style: 'fine-line detailed coloring page, thin uniform black line, patterned robes, leaves and waves for older children', palette: 'black ink only', framing: 'The scene held inside a plain thin rectangular border with a white margin outside it'},
    ],
    directions: ['more lambs', 'rocky hills', 'night search'],
    directionHint: 'try: more lambs',
    seo: {
        title: 'Lost Sheep Coloring Pages: Luke 15 in Three Scenes',
        metaDescription: 'Nine lost sheep coloring pages for Sunday school from Luke 15: the flock counted, the lamb found, carried home, in three styles.',
        h1: 'Lost sheep coloring pages: the ninety-nine, searching, carried home',
        tagline: 'Nine pages of the parable of the lost sheep in three scenes, each in three line weights.',
        intro: 'Printable lost sheep coloring pages for Sunday school and Christian homeschool, the parable in Luke 15. Three scenes: a shepherd counting his flock with one place empty, the shepherd searching among rocks and thorns for the lamb, and carrying it home on his shoulders while friends rejoice. Bold, classic and fine-detail lines. Type a different scene, and the same styles carry it.',
        useCases: [
            'a Sunday school lesson on Luke 15',
            "a children's church craft table",
            'a Christian homeschool Gospel unit',
            'a bulletin board on the shepherd and the lamb',
        ],
        faq: [
            {q: 'Which part of the parable do these show?', a: 'The shepherd counting the flock, the search for the lamb, and the return home.'},
            {q: 'Can I ask for more lambs or a night search?', a: 'Yes. Type the scene you want. The request shapes the page, but a result is not promised to match it exactly.'},
            {q: 'Which style suits a young child?', a: 'Bold and simple, with one heavy line and big open shapes.'},
            {q: 'Are the pages in color?', a: 'No. Each page is black line on white, with open shapes to color in.'},
        ],
    },
}
